<template>
  <div class="nave-select" :class="{ bordered: borderStyle }">
    <div
      class="nave-select__label"
      :class="{ bordered: borderStyle }"
      v-on="eventList"
      data-test-id="nave-select__button"
    >
      <span>{{ label ? label : placeholder }}</span>
      <i class="icon-nav-bullet_down" />
    </div>

    <ul
      ref="listOptions"
      class="nave-select__list"
      data-test-id="nave-select__list"
      v-if="showSelectOptions"
      @mouseleave="mouseLeaveSelectOptions"
      @mouseenter="mouseEnterSelectOptions"
    >
      <p v-if="options.header" data-test-id="nave-select__header">
        {{ options.header }}
        <i class="icon-nav-bullet_up" />
      </p>

      <div v-if="showSearchInput" class="nave-select__search-area mb-2">
        <span class="p-input-icon-left">
          <IconField iconPosition="left" class="xp-input-icon">
            <InputIcon>
              <i class="icon-nav-search" />
            </InputIcon>
            <InputText
              type="text"
              v-model="searchOption"
              placeholder="Buscar loja"
              data-test-id="nave-select__input"
            />
          </IconField>
        </span>
      </div>

      <template v-if="getType(options.options) === 'object'">
        <template v-for="(option, index) in filteredOptions" :key="index">
          <li @click="optionSelected(index, option)">{{ option }}</li>
        </template>
      </template>
      <template v-else v-for="(option, index) in filteredOptions" :key="index">
        <li
          @click="optionSelected(option.value, option.text)"
          class="d-flex align-items-center"
          data-test-id="nave-select__option"
        >
          <i v-if="option.icon" :class="[option.icon]" />
          <span>{{ option.text }}</span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  PropType,
  computed,
  nextTick,
  watch,
} from "vue";

type IOptions = {
  header?: string;
  placeholder?: string;
  options: { [prop: string]: string } | { value: any; text: string }[];
};

type IEventList = {
  click: () => void;
  mouseleave: null | (() => void);
  mouseenter: null | (() => void);
};

export default defineComponent({
  name: "NaveSelect",

  props: {
    options: { type: Object as PropType<IOptions>, required: true },
    filled: { type: Boolean, default: true },
    modelValue: { type: [String, Number], default: "" },
    borderStyle: { type: Boolean, default: false },
    showSearchInput: { type: Boolean, default: false },
  },

  setup(props, context) {
    const showSelectOptions = ref<boolean>(false);
    const searchOption = ref<string>("");
    const label = ref<string>("");
    const listOptions = ref<HTMLElement | null>(null);

    const placeholder = computed(() => {
      return props.options.placeholder || "";
    });

    const filteredOptions = computed(() => {
      if (searchOption.value) {
        if (getType(props.options.options) === "object") {
          (Reflect as any).filter = (obj, predicate) => {
            let keys = Reflect.ownKeys(obj);

            let filter = keys.filter((key) => {
              return predicate(obj[key]);
            });

            return filter.reduce((res, key) => {
              return (res[key] = obj[key]), res;
            }, {});
          };

          let filtered = (Reflect as any).filter(
            props.options.options,
            (el) => {
              return el.toLocaleLowerCase().includes(searchOption.value);
            }
          );

          return filtered;
        } else {
          return (
            props.options.options as { value: any; text: string }[]
          ).filter((option: any) => {
            return option.text
              .toLowerCase()
              .includes(searchOption.value.toLocaleLowerCase());
          });
        }
      }

      return props.options.options;
    });

    const openSelect = () => {
      window.removeEventListener("click", openSelect, false);

      showSelectOptions.value = !showSelectOptions.value;

      if (showSelectOptions.value) {
        nextTick(() => {
          checkElementVisibleScreen(listOptions.value);
        });

        eventList.value.mouseleave = mouseLeaveSelectOptions;
        eventList.value.mouseenter = mouseEnterSelectOptions;
      } else {
        eventList.value.mouseleave = null;
        eventList.value.mouseenter = null;
      }
    };

    const mouseLeaveSelectOptions = () => {
      window.addEventListener("click", openSelect, false);
    };

    const mouseEnterSelectOptions = () => {
      window.removeEventListener("click", openSelect, false);
    };

    const eventList = ref<IEventList>({
      click: openSelect,
      mouseleave: null,
      mouseenter: null,
    });

    const optionSelected = (option: string, labelToShow: string) => {
      label.value = labelToShow;
      context.emit("optionSelected", option);
      context.emit("update:modelValue", option);

      window.removeEventListener("click", openSelect, false);
      eventList.value.mouseleave = null;
      eventList.value.mouseenter = null;
      showSelectOptions.value = !showSelectOptions.value;
    };

    const getType = (data: any) => {
      return Object.prototype.toString
        .call(data)
        .replace(/(\[object )?(\])?/g, "")
        .toLowerCase();
    };

    const checkElementVisibleScreen = (divElement: HTMLElement | null) => {
      if (divElement) {
        const sizeRange = 30;
        let divElementDementions = divElement.getBoundingClientRect();
        let currentDementions = {
          bottom: divElementDementions.bottom + sizeRange,
          right: divElementDementions.right + sizeRange,
        };
        let fullSizeToGoBackTop = 0;
        let fullSizeToGoBackLeft = 0;

        do {
          fullSizeToGoBackTop +=
            currentDementions.bottom >= window.innerHeight ? sizeRange : 0;
          fullSizeToGoBackLeft +=
            currentDementions.right >= window.innerWidth ? sizeRange : 0;
          divElement.style.transform = `translate(-${fullSizeToGoBackLeft}px, -${fullSizeToGoBackTop}px)`;
          divElementDementions = divElement.getBoundingClientRect();
          currentDementions.bottom = divElementDementions.bottom + sizeRange;
          currentDementions.right = divElementDementions.right + sizeRange;
        } while (
          currentDementions.bottom >= window.innerHeight ||
          currentDementions.right >= window.innerWidth
        );
      }
    };

    watch(
      () => props.modelValue,
      () => {
        if (props.modelValue === null) label.value = "";
      }
    );

    onMounted(() => {
      if (props.filled && getType(props.options.options) === "object") {
        label.value =
          props.options.options[Object.keys(props.options.options)[0]];
      } else if (props.filled && getType(props.options.options) === "array") {
        const valueText = (
          props.options.options as { value: any; text: string }[]
        ).find((el) => {
          return el.value === props.modelValue;
        });

        label.value = valueText ? valueText.text : "";
      }
    });

    return {
      label,
      showSelectOptions,
      filteredOptions,
      searchOption,
      optionSelected,
      openSelect,
      mouseLeaveSelectOptions,
      mouseEnterSelectOptions,
      placeholder,
      getType,
      listOptions,
      eventList,
    };
  },
});
</script>

<style lang="scss">
.nave-select {
  position: relative;

  &.bordered {
    border-radius: 3px;
    height: toRem(42.5px);
    padding: toRem(7px) toRem(10px);
    border: 2px solid var(--insights-color-15);
  }

  &__label {
    cursor: pointer;
    font-size: 1rem;
    color: var(--insights-color-16);
    display: flex;
    align-items: baseline;

    > i {
      font-size: toRem(11px);
      margin-left: 10px;
    }

    &.bordered {
      font-style: italic;
      font-size: toRem(16px);
      font-family: "text-medium";
      color: var(--insights-color-16);
    }
  }

  &__search-area {
    display: flex;
    justify-content: space-between;

    > span {
      &:first-child {
        flex-basis: 260px;
        input {
          width: 100%;
        }
      }
    }

    .p-inputtext {
      border: 2px solid #e3e3e3;
      border-radius: 3px;
      font-family: "text-medium";
      font-size: toRem(15px);
      color: var(--insights-color-1);

      &::placeholder {
        color: #a2a2a2;
        font-family: "text-medium";
        font-style: italic;
      }
    }
  }

  &__list {
    list-style: none;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 100;
    background: var(--insights-color-2);
    width: toRem(253px);
    padding: 1.1rem 1.3rem;
    box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    color: var(--insights-color-16);
    font-size: toRem(15px);

    p {
      text-transform: uppercase;
      font-size: toRem(13px);
      color: var(--insights-color-16);
      cursor: default;
      font-family: "text-bold";
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;

      > i {
        font-size: toRem(11px);
      }
    }

    li {
      cursor: pointer;
      margin-bottom: toRem(13px);
      gap: toRem(10px);

      &:hover {
        color: #7293c3;

        > i {
          color: #7293c3;
        }
      }

      &:last-child {
        margin-bottom: 0px;
      }

      > i {
        color: #c9c9c9;
      }
    }
  }
}
</style>
